// src/pages/AllTestsPage/AllTestsPage.tsx

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Stack,
  CircularProgress,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FileSpreadsheet } from "lucide-react";

/* -----------------------------------------
   Table Component
----------------------------------------- */
interface TableComponentProps {
  data: any[];
  columns: string[];
  columnLabels: Record<string, string>;
}

const TableComponent: React.FC<TableComponentProps> = ({
  data,
  columns,
  columnLabels,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc" | null;
  }>({
    key: "",
    direction: null,
  });

  const mobileColumns = [
    "healthCenter",
    "caseId",
    "diagnosisResults",
    "gender",
    "timestamp",
  ];
  const tabletColumns = [
    "healthCenter",
    "deviceId",
    "caseId",
    "age",
    "gender",
    "diagnosisResults",
    "parasiteSpecies",
    "location",
    "timestamp",
  ];
  const desktopColumns = columns;
  const activeColumns = isMobile
    ? mobileColumns
    : isTablet
    ? tabletColumns
    : desktopColumns;

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    const dir = sortConfig.direction === "asc" ? 1 : -1;
    return [...data].sort((a, b) => {
      if (a[sortConfig.key] > b[sortConfig.key]) return dir;
      if (a[sortConfig.key] < b[sortConfig.key]) return -dir;
      return 0;
    });
  }, [data, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const renderCellValue = (col: string, row: any) => {
    if (col === "timestamp") return new Date(row[col]).toLocaleString();
    if (col === "parasiteDensity" && row[col]) {
      const lower = row[col].ci?.[0];
      const upper = row[col].ci?.[1];
      if (lower != null && upper != null)
        return `${lower}–${upper} parasites/µL`;
      if (row[col].value != null) return `${row[col].value} parasites/µL`;
      return "N/A";
    }
    return row[col] ?? "-";
  };

  return (
    <Box
      sx={{
        overflowX: "auto",
        borderRadius: 1,
        // Prevent horizontal scroll on page by allowing table to scroll internally
        width: "100%",
      }}
    >
      <table
        style={{
          fontSize: isTablet ? "0.75rem" : "0.875rem",
          backgroundColor: "#273142",
          width: "100%",
          minWidth: 800, // Ensures table has minimum width but allows internal scroll on small screens
          borderCollapse: "collapse",
        }}
      >
        <thead
          style={{
            position: "sticky",
            top: 0,
            backgroundColor: "#273142",
            zIndex: 1,
          }}
        >
          <tr>
            {activeColumns.map((col) => (
              <th
                key={col}
                onClick={() => handleSort(col)}
                style={{
                  padding: isTablet ? "6px 8px" : "8px 12px",
                  cursor: "pointer",
                  fontWeight: 700,
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  color: "#E5E7EB",
                  borderBottom: `1px solid #374151`,
                }}
              >
                {columnLabels[col] || col}
                {sortConfig.key === col &&
                  (sortConfig.direction === "asc" ? " ▲" : " ▼")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, i) => (
            <tr key={i}>
              {activeColumns.map((col) => {
                const isDiagnosis = col === "diagnosisResults";
                const isPositive = isDiagnosis && row[col] === "Positive";

                return (
                  <td
                    key={col}
                    style={{
                      padding: isTablet ? "6px 8px" : "8px 12px",
                      borderTop: "1px solid rgba(255,255,255,0.08)",
                      whiteSpace: "nowrap",
                      color: "#E5E7EB",
                      backgroundColor: "transparent",
                    }}
                  >
                    {isDiagnosis ? (
                      /* ✅ Pill-style badge (matches Box design) */
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "4px 12px",
                          borderRadius: 999,
                          fontSize: 13,
                          fontWeight: 500,
                          color: isPositive ? "#EF4444" : "#5EC16A",
                          backgroundColor: isPositive
                            ? "rgba(239,68,68,0.24)"
                            : "rgba(94,193,106,0.24)",
                          textShadow: isPositive
                            ? "0 0 10px rgba(239,68,68,0.25)"
                            : "0 0 10px rgba(94,193,106,0.25)",
                        }}
                      >
                        {renderCellValue(col, row)}
                      </span>
                    ) : (
                      /* Normal cells (unchanged behavior) */
                      <span
                        style={{
                          color: "rgba(255,255,255,0.6)",
                          fontSize: 14,
                        }}
                      >
                        {renderCellValue(col, row)}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

/* -----------------------------------------
   Main Page Component
----------------------------------------- */
const AllTestsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const [tests, setTests] = useState<any[]>([]);
  const [filters, setFilters] = useState({
    status: "",
    gender: "",
    location: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [lastUpdated, setLastUpdated] = useState<string>("");

  /* -------- Debounce Search Input -------- */
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  /* -------- Load Cached Data (24h) -------- */
  useEffect(() => {
    const cached = sessionStorage.getItem("tests");
    const cachedTime = sessionStorage.getItem("tests_timestamp");
    const isCacheValid =
      cached &&
      cachedTime &&
      Date.now() - parseInt(cachedTime) < 24 * 60 * 60 * 1000;

    if (isCacheValid) {
      setTests(JSON.parse(cached));
      setLastUpdated(new Date(parseInt(cachedTime!)).toLocaleString());
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}/api/tests`);
        const data = await res.json();
        setTests(data);
        const now = Date.now().toString();
        sessionStorage.setItem("tests", JSON.stringify(data));
        sessionStorage.setItem("tests_timestamp", now);
        setLastUpdated(new Date(parseInt(now)).toLocaleString());
      } catch (err) {
        console.error("Error fetching tests:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_BASE_URL]);

  /* -------- Persistent Filters -------- */
  useEffect(() => {
    const saved = localStorage.getItem("allTestsFilters");
    if (saved) setFilters(JSON.parse(saved));
  }, []);

  useEffect(() => {
    localStorage.setItem("allTestsFilters", JSON.stringify(filters));
  }, [filters]);

  /* -------- Search + Filtering -------- */
  const filteredTests = useMemo(() => {
    return tests.filter((test) => {
      const matchesFilters =
        (filters.status === "" || test.diagnosisResults === filters.status) &&
        (filters.gender === "" || test.gender === filters.gender) &&
        (filters.location === "" || test.location === filters.location);

      const matchesSearch =
        debouncedSearch === "" ||
        Object.values({
          caseId: test.caseId,
          healthCenter: test.healthCenter,
          location: test.location,
          parasiteSpecies: test.parasiteSpecies,
        })
          .join(" ")
          .toLowerCase()
          .includes(debouncedSearch.toLowerCase());

      return matchesFilters && matchesSearch;
    });
  }, [tests, filters, debouncedSearch]);

  const totalPages = Math.ceil(filteredTests.length / pageSize);
  const paginatedTests = filteredTests.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  useEffect(() => setPage(1), [filters, pageSize, debouncedSearch]);

  /* -------- CSV Export -------- */
  const exportCSV = () => {
    const headers = [
      "Health Center",
      "Device ID",
      "Case ID",
      "Age",
      "Gender",
      "Diagnosis",
      "Species",
      "Parasite Density (Range)",
      "Drug Resistance",
      "Location",
      "Timestamp",
    ];

    const rows = filteredTests.map((t) => [
      t.healthCenter,
      t.deviceId,
      t.caseId,
      t.age,
      t.gender,
      t.diagnosisResults,
      t.parasiteSpecies,
      t.parasiteDensity?.ci
        ? `${t.parasiteDensity.ci[0]}–${t.parasiteDensity.ci[1]} parasites/µL`
        : t.parasiteDensity?.value
        ? `${t.parasiteDensity.value} parasites/µL`
        : "N/A",
      t.drugResistance,
      t.location,
      new Date(t.timestamp).toLocaleString(),
    ]);

    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "MariTest_AllTests.csv";
    link.click();
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: "#1B2431",
        minHeight: "100vh",
        // Prevent outer page scroll when table scrolls
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={{ xs: 2, sm: 3 }}
        mb={3}
      >
        <Typography
          variant={isMobile ? "h6" : "h5"}
          sx={{ fontWeight: 700, color: "#FFFFFF" }}
        >
          All Tests
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          {lastUpdated && (
            <Typography variant="caption" color="#91969E">
              Last updated: {lastUpdated}
            </Typography>
          )}
          <Button
            variant="outlined"
            startIcon={<FileSpreadsheet size={16} />}
            onClick={exportCSV}
            sx={{
              color: "#FFFFFF",
              borderColor: "#4B5563",
              "&:hover": {
                borderColor: "#5E6B7F",
                bgcolor: "rgba(255,255,255,0.08)",
              },
            }}
          >
            Export CSV
          </Button>
        </Stack>
      </Stack>

      {/* Compact Search & Filters - Fixed Width Search */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        mb={3}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <TextField
          size="small"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            width: { xs: "100%", sm: 300, md: 320 },
            "& .MuiOutlinedInput-root": {
              bgcolor: "#323D4E",
              color: "#E5E7EB",
              "& fieldset": { borderColor: "#4B5563" },
              "&:hover fieldset": { borderColor: "#5E6B7F" },
            },
          }}
        />

        <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 140 } }}>
          <InputLabel sx={{ color: "#91969E" }}>Status</InputLabel>
          <Select
            value={filters.status}
            onChange={(e) =>
              setFilters({ ...filters, status: e.target.value as string })
            }
            sx={{
              bgcolor: "#323D4E",
              color: "#E5E7EB",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#4B5563" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#5E6B7F",
              },
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Positive">Positive</MenuItem>
            <MenuItem value="Negative">Negative</MenuItem>
            <MenuItem value="Indeterminate">Indeterminate</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 140 } }}>
          <InputLabel sx={{ color: "#91969E" }}>Gender</InputLabel>
          <Select
            value={filters.gender}
            onChange={(e) =>
              setFilters({ ...filters, gender: e.target.value as string })
            }
            sx={{
              bgcolor: "#323D4E",
              color: "#E5E7EB",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#4B5563" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#5E6B7F",
              },
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 160 } }}>
          <InputLabel sx={{ color: "#91969E" }}>Location</InputLabel>
          <Select
            value={filters.location}
            onChange={(e) =>
              setFilters({ ...filters, location: e.target.value as string })
            }
            sx={{
              bgcolor: "#323D4E",
              color: "#E5E7EB",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#4B5563" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#5E6B7F",
              },
            }}
          >
            <MenuItem value="">All</MenuItem>
            {Array.from(new Set(tests.map((t) => t.location))).map((loc) => (
              <MenuItem key={loc} value={loc}>
                {loc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Table */}
      <Card sx={{ borderRadius: 2, bgcolor: "#273142", overflow: "hidden" }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 }, pb: 3 }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 200,
              }}
            >
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <>
              <TableComponent
                data={paginatedTests}
                columns={[
                  "healthCenter",
                  "deviceId",
                  "caseId",
                  "age",
                  "gender",
                  "diagnosisResults",
                  "parasiteSpecies",
                  "parasiteDensity",
                  "drugResistance",
                  "location",
                  "timestamp",
                ]}
                columnLabels={{
                  healthCenter: "Health Center",
                  deviceId: "Device ID",
                  caseId: "Case ID",
                  age: "Age",
                  gender: "Gender",
                  diagnosisResults: "Diagnosis",
                  parasiteSpecies: "Species",
                  parasiteDensity: "Parasite Density (Range)",
                  drugResistance: "Drug Resistance",
                  location: "Location",
                  timestamp: "Timestamp",
                }}
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="center"
                  alignItems="center"
                  spacing={2}
                  mt={3}
                >
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Rows</InputLabel>
                    <Select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      sx={{
                        bgcolor: "#323D4E",
                        color: "#E5E7EB",
                      }}
                    >
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                      <MenuItem value={200}>200</MenuItem>
                      <MenuItem value={500}>500</MenuItem>
                    </Select>
                  </FormControl>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={page === 1}
                      onClick={() => setPage(1)}
                      sx={{ color: "#E5E7EB", borderColor: "#4B5563" }}
                    >
                      First
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(p - 1, 1))}
                      sx={{ color: "#E5E7EB", borderColor: "#4B5563" }}
                    >
                      Prev
                    </Button>
                    <Typography color="#E5E7EB">
                      Page {page} of {totalPages}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={page === totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(p + 1, totalPages))
                      }
                      sx={{ color: "#E5E7EB", borderColor: "#4B5563" }}
                    >
                      Next
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={page === totalPages}
                      onClick={() => setPage(totalPages)}
                      sx={{ color: "#E5E7EB", borderColor: "#4B5563" }}
                    >
                      Last
                    </Button>
                  </Stack>
                </Stack>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AllTestsPage;
